import { FC, useCallback, useRef, useState } from 'react';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import { AddressInfo, GoogleAutocomplete, GoogleMap } from 'ui-kit/lib/address';
import { inheritDataTestId } from '@/utils/testId.ts';

export interface AddressLookupProps {
  sx?: SxProps<Theme>;
  onChange?: (addr: AddressInfo | null) => void;
  error?: string;
  initialValue?: { addressString?: string; googlePlaceId?: string };
  readonly?: boolean;
  placeholder: string;
  name?: string;
}

export const AddressLookup: FC<AddressLookupProps> = ({
  placeholder,
  initialValue,
  readonly,
  sx,
  onChange,
  error,
  ...props
}) => {
  const [placeId, setPlaceId] = useState<string | undefined>(
    initialValue?.googlePlaceId ?? undefined
  );
  const resetHandlerRef = useRef({ reset: () => {} });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const handleAutocomplete = useCallback((addr: AddressInfo | null) => {
    if (addr?.googlePlaceId) {
      setPlaceId(addr.googlePlaceId);
    }
    onChangeRef.current?.(addr);
  }, []);

  return (
    <>
      <Stack
        sx={sx}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        {!readonly && (
          <Box sx={{ pb: { xs: 24 } }}>
            <GoogleAutocomplete
              data-test-id={inheritDataTestId(
                props,
                '_google-autocomplete-combobox'
              )}
              onChange={handleAutocomplete}
              error={error}
              placeholder={placeholder}
              searchBy={
                initialValue?.googlePlaceId
                  ? { placeId: initialValue.googlePlaceId }
                  : initialValue?.addressString
                    ? { value: initialValue.addressString }
                    : undefined
              }
            />
          </Box>
        )}
        <GoogleMap
          data-test-id={inheritDataTestId(props, '_google-autocomplete-map')}
          searchBy={
            placeId
              ? { placeId }
              : initialValue?.addressString
                ? { value: initialValue.addressString }
                : undefined
          }
          innerRef={resetHandlerRef}
        />
      </Stack>
    </>
  );
};
